/**
 * Shared helpers for the LK Shop pages: money and date formatting, the
 * listing-filter ⇄ URL mapping, and the status vocabulary for shipments and
 * returns.
 */

/* ---------------------------------------------------------------- money */

/** ₹ with Indian digit grouping (₹1,29,999). Paise only when there are any. */
export const rupees = (value) => {
  const n = Number(value || 0);
  const whole = Math.round(n * 100) % 100 === 0;
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
};

/* ---------------------------------------------------------------- dates */

const IST = 'Asia/Kolkata';

/** "6 Nov 2026" in IST. Pass Intl options to change the shape. */
export const istDate = (iso, opts = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { timeZone: IST, ...opts }).format(d);
};

/** "Fri, 6 Nov" in IST. */
export const istDay = (iso) => istDate(iso, { weekday: 'short', day: 'numeric', month: 'short' });

/** "6 Nov, 2:05 pm" in IST. */
export const istDateTime = (iso) =>
  istDate(iso, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });

/* --------------------------------------------------------- idempotency */

/** One key per checkout / cancel attempt. 8–60 chars; the backend dedupes retries on it. */
export const newIdempotencyKey = (prefix = 'web') => {
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${rand}`.slice(0, 56);
};

/* ------------------------------------------------------------- filters */

/**
 * The listing page keeps every filter in the URL with the API's own names, so
 * the query string is sent through unchanged and a shared link reproduces the
 * exact view. Brand, rating and discount are top-level params; price is a
 * min/max pair; every other facet (colour, size, fabric…) is `f[key]`.
 */
export const TOP_LEVEL_FACETS = new Set(['brand', 'rating', 'discount']);

export const facetParam = (key) => (TOP_LEVEL_FACETS.has(key) ? key : `f[${key}]`);

export const readMulti = (params, key) =>
  (params.get(facetParam(key)) || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/** Applied filters as removable chips, in facet order, labelled from the facets. */
export const appliedChips = (params, facets = []) => {
  const chips = [];
  for (const f of facets) {
    if (f.type === 'range') {
      const min = params.get('priceMin');
      const max = params.get('priceMax');
      if (min || max) {
        const label = min && max ? `${rupees(min)} – ${rupees(max)}` : min ? `Over ${rupees(min)}` : `Under ${rupees(max)}`;
        chips.push({ id: 'price', label, remove: (p) => (p.delete('priceMin'), p.delete('priceMax')) });
      }
      continue;
    }
    if (f.type === 'single') {
      const v = params.get(f.key);
      if (v) {
        const opt = f.options?.find((o) => String(o.value) === v);
        chips.push({ id: f.key, label: opt?.label || `${f.label}: ${v}`, remove: (p) => p.delete(f.key) });
      }
      continue;
    }
    const values = readMulti(params, f.key);
    for (const v of values) {
      const opt = f.options?.find((o) => String(o.value) === v);
      chips.push({
        id: `${f.key}:${v}`,
        label: opt?.label || v,
        hex: f.type === 'color' ? opt?.hex : null,
        remove: (p) => {
          const rest = values.filter((x) => x !== v);
          if (rest.length) p.set(facetParam(f.key), rest.join(','));
          else p.delete(facetParam(f.key));
        },
      });
    }
  }
  return chips;
};

/** Every filter param (not q, sort or page) — what "Clear all" removes. */
export const isFilterParam = (key) =>
  key === 'priceMin' || key === 'priceMax' || TOP_LEVEL_FACETS.has(key) || /^f\[.+\]$/.test(key);

export const SORT_LABELS = {
  relevance: 'Relevance',
  popular: 'Popularity',
  price_asc: 'Price: low to high',
  price_desc: 'Price: high to low',
  newest: 'Newest first',
  discount: 'Discount',
  rating: 'Customer rating',
};

/* --------------------------------------------------- colour contrast */

/** True when a swatch is so light it needs a border to be visible on white. */
export const isLightHex = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 215;
};

/* -------------------------------------------------- shipment statuses */

export const SHIPMENT_STEPS = [
  { key: 'pending', label: 'Ordered' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'packed', label: 'Packed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];

export const SHIPMENT_LABELS = {
  awaiting_payment: 'Awaiting payment',
  pending: 'Order placed',
  accepted: 'Accepted by seller',
  packed: 'Packed',
  shipped: 'Shipped',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned_to_origin: 'Returned to seller',
};

/** Before this point the customer can still cancel items. */
export const CANCELLABLE_SHIPMENT = new Set(['pending', 'accepted', 'packed']);

export const shipmentTone = (status) => {
  if (status === 'delivered') return 'success';
  if (status === 'cancelled' || status === 'returned_to_origin') return 'danger';
  if (status === 'shipped' || status === 'out_for_delivery') return 'info';
  return 'warning';
};

export const ORDER_LABELS = {
  pending_payment: 'Payment pending',
  confirmed: 'Confirmed',
  partially_cancelled: 'Partly cancelled',
  cancelled: 'Cancelled',
  completed: 'Completed',
  expired: 'Payment not completed',
  refunded: 'Refunded',
};

/* ----------------------------------------------------------- returns */

export const RETURN_REASONS = [
  { value: 'damaged', label: 'Item arrived damaged' },
  { value: 'wrong_item', label: 'Received the wrong item' },
  { value: 'not_as_described', label: 'Not as described' },
  { value: 'size_fit', label: "Size or fit isn't right" },
  { value: 'quality', label: 'Quality not as expected' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'other', label: 'Other' },
];

export const RETURN_LABELS = {
  requested: 'Return requested',
  approved: 'Return approved',
  picked_up: 'Picked up',
  received: 'Received by seller',
  refunded: 'Refunded',
  rejected: 'Return rejected',
  cancelled: 'Return cancelled',
};

export const RETURN_STEPS = ['requested', 'approved', 'picked_up', 'received', 'refunded'];

/* ----------------------------------------------------- variant states */

/**
 * Option states, worked out for the current selection:
 *  - selected      the chosen value
 *  - available     same other options, in stock
 *  - out_of_stock  that exact combination exists but has no stock (still pickable, says so)
 *  - unavailable   no such combination with the other options; picking it jumps to the
 *                  closest variant that has it (the backend's `variantId`)
 * The backend sends these for the variant it rendered; when the shopper switches
 * variant in the page we recompute them locally so the change is instant.
 */
export const deriveOptionStates = (theme = [], variants = [], selected) => {
  if (!selected) return {};
  // Mirrors the backend's optionStates (rules.ts) exactly, so the chips do not
  // reorder between the server's first render and the first local tap.
  const keys = theme.map((a) => a.key);
  const sel = selected.options || {};
  const out = {};
  for (const axis of theme) {
    // One stable order: the category's option list (S, M, L…), then A–Z.
    const order = (axis.options || []).map((o) => String(o.value));
    const rank = (x) => {
      const i = order.indexOf(String(x));
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    const values = [...new Set(variants.map((v) => v.options?.[axis.key]).filter(Boolean))].sort(
      (a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b))
    );
    out[axis.key] = values.map((value) => {
      if (sel[axis.key] === value) return { value, state: 'selected', variantId: selected.id };
      const want = { ...sel, [axis.key]: value };
      const exact = variants.find((v) => keys.every((k) => v.options?.[k] === want[k]));
      if (exact) return { value, state: exact.inStock ? 'available' : 'out_of_stock', variantId: exact.id };
      // Nearest: same value on this axis, most other axes matching, in stock first.
      const near = variants
        .filter((v) => v.options?.[axis.key] === value)
        .map((v) => ({ v, score: keys.filter((k) => v.options?.[k] === sel[k]).length * 2 + (v.inStock ? 1 : 0) }))
        .sort((a, b) => b.score - a.score)[0];
      return { value, state: 'unavailable', variantId: near?.v.id ?? null };
    });
  }
  return out;
};

/** Units of an order line still with the customer (not cancelled or returned). */
export const activeQty = (item) => Math.max(0, item.qty - (item.cancelledQty || 0) - (item.returnedQty || 0));
